import { useEffect } from 'react';

// Shared layout-skin system. Owner/principal pick these in their Background
// (EngineerProfile LayoutThemePicker); the detailer picks them in the Detailer
// Background tab. Each is a `body.layout-*` class; the pick is persisted
// per-role (so two roles on one device don't bleed) and a `3c-layout-changed`
// event applies it live. (Dashboard.jsx / EngineerProfile.jsx still carry their
// own copies of this list for owner/principal — this module is the canonical one
// the detailer path uses.)
export const LAYOUTS = ['layout-soft', 'layout-brutal', 'layout-glass', 'layout-synth', 'layout-carbon', 'layout-luxe', 'layout-ocean', 'layout-signal'];

// `only: <role>` restricts an option to one profile's picker. Signal is the
// principal's own skin — filter every picker with visibleLayoutOptions(role).
export const LAYOUT_OPTIONS = [
  { id: '',               label: 'Default',   blurb: 'Stock 3CHA look.' },
  { id: 'layout-soft',    label: 'Plush',     blurb: 'Deep blue pillows, 22px curves, lift-on-hover, halo shadows.' },
  { id: 'layout-brutal',  label: 'Brutalist', blurb: 'Hard 5px offset shadows, monospace everywhere, all-caps, zero radius.' },
  { id: 'layout-glass',   label: 'Glass',     blurb: 'Frosted-blue cards w/ neon edge glow, radial dark backdrop.' },
  { id: 'layout-synth',   label: 'Synth-CRT', blurb: 'Amber phosphor terminal — scanlines, screen glow, mono text.' },
  { id: 'layout-carbon',  label: 'Carbon',    blurb: 'Carbon-fiber charcoal + racing red. Sharp, sporty, automotive.' },
  { id: 'layout-luxe',    label: 'Luxe',      blurb: 'Matte black + brushed gold — premium, matches the booking page.' },
  { id: 'layout-ocean',   label: 'Ocean',     blurb: 'Deep teal + cyan glow, soft 16px curves. Calm and clean.' },
  { id: 'layout-signal',  label: 'Signal',    blurb: 'Refined brutalism — crimson + emerald on white, hard offset shadows.', only: 'principal' },
];

// The skins a given role may pick. Options tagged `only` are hidden from
// everyone else, so Signal shows up in the principal's picker alone.
export function visibleLayoutOptions(role) {
  return LAYOUT_OPTIONS.filter((o) => !o.only || o.only === role);
}

// Per-role localStorage key so each role on a shared device keeps its own skin.
export function layoutKeyForRole(role) {
  return role === 'owner' ? '3c_owner_layout'
    : role === 'principal' ? '3c_principal_layout'
    : role === 'detailer' ? '3c_detailer_layout'
    : '';
}

// Reflect the given role's saved skin onto <body> (clears all others first).
export function applyLayoutForRole(role) {
  LAYOUTS.forEach((c) => document.body.classList.remove(c));
  const key = layoutKeyForRole(role);
  if (!key) return;
  try {
    const saved = localStorage.getItem(key) || '';
    if (LAYOUTS.includes(saved)) document.body.classList.add(saved);
  } catch (_) {}
}

// Persist a pick for a role + fire the same-tab apply signal. '' = Default.
export function setLayoutForRole(role, id) {
  const key = layoutKeyForRole(role);
  try {
    if (id) localStorage.setItem(key, id); else localStorage.removeItem(key);
    // Device-wide mirror so the login page (no role yet) can match the last skin.
    if (id) localStorage.setItem('3c_last_layout', id); else localStorage.removeItem('3c_last_layout');
  } catch (_) {}
  try { window.dispatchEvent(new Event('3c-layout-changed')); } catch (_) {}
}

// Keep <body> in sync with a role's skin for the lifetime of a page, reacting to
// live picks (same-tab event) + cross-tab storage changes. Clears on unmount.
export function useApplyLayout(role) {
  useEffect(() => {
    applyLayoutForRole(role);
    const sync = () => applyLayoutForRole(role);
    const onStorage = (e) => { if (!e || !e.key || e.key === layoutKeyForRole(role)) sync(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('3c-layout-changed', sync);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('3c-layout-changed', sync);
      LAYOUTS.forEach((c) => document.body.classList.remove(c));
    };
  }, [role]);
}
